import * as zmq from 'zeromq';
import { existsSync, unlinkSync } from 'fs';
import { BaseServerConfig, ServerProtocolArray } from './BaseServer.js';
import { isCurveKey } from './trust/keys.js';
import { ZapAuthority } from './trust/zap.js';
import { ipcSocketPath } from './trust/ipcDir.js';

/**
 * Receives one decoded envelope and the id of the PEER WHOSE SOCKET it arrived
 * on. That second argument is the authenticated sender: each peer has its own
 * CURVE-pinned subscriber, so nothing in the payload can change it.
 */
export type PubSubMessageHandler = (e: ServerProtocolArray, fromPeerId: string) => void;

/**
 * One server's zmq transport, authenticated and encrypted with CurveZMQ.
 *
 *  - The PUBLISHER is a CURVE server. Its ZAP domain admits only the public keys
 *    of this server's peer set — the peers it has `connect()`ed — so a socket
 *    holding any other key (or none) completes no handshake and reads nothing.
 *  - Each peer gets its OWN SUBSCRIBER, a CURVE client pinned to that peer's
 *    public key. A message's sender is therefore the socket it arrived on;
 *    `onSubMessage` is told which, and BaseServer drops any payload claiming
 *    otherwise. (One shared subscriber made the sender a payload field anyone
 *    admitted could forge.)
 *  - The topic frame must equal this server's id EXACTLY: zmq subscriptions
 *    are prefix matches, so `area1` would otherwise also receive `area10`'s.
 */
export class PubSub {
    public publisher: zmq.Publisher;
    /** One CURVE-pinned subscriber per connected peer, keyed by peer id. */
    public subscribers : {[id: string]: zmq.Subscriber } = {}
    readonly id: string | number;
    readonly config : BaseServerConfig<any>;
    readonly onSubMessage: PubSubMessageHandler;
    /** This publisher's ZAP domain (unique in the process). */
    readonly zapDomain: string;
    private zapOpen = false;
    readonly connected : Array<BaseServerConfig<any>> = [];
    private boundIpcSocket : boolean = false;
    private boundTcpSocket : boolean = false;
    private isBindingIpcSocket : boolean = false;
    private isBindingTcpSocket : boolean = false;
    private wasUsingBindingPub : boolean = false;
    /**
     * Serializes EVERY socket lifecycle transition (bind/unbind/close). The
     * pre-2026 code guarded transitions with `isBinding*` flags and SKIPPED a
     * required bind whenever another op was in flight — including an unbind —
     * with nothing ever re-evaluating afterwards. A publisher whose needed bind
     * was skipped has no endpoint, and a pub socket with no endpoint drops
     * every send silently, so the peer's registration retry loop burned its
     * whole ~23s budget: the five-year "Timed out trying to send registration"
     * flake. The interleave (`removeConnection` starts an unbind; `connect`
     * lands inside its ~1ms await window) was captured live by instrumentation,
     * same-millisecond, in AreaInstances.remote.
     *
     * Every mutation goes through `convergeSockets`, which re-reads the needs
     * AFTER each completed op and loops until the socket state matches them —
     * so a connect that lands mid-unbind converges to bound instead of being
     * dropped on the floor. See PubSub.lifecycle.test.ts, which reproduces the
     * interleave deterministically against real zmq sockets.
     */
    private socketOpChain : Promise<void> = Promise.resolve();
    /** Set once by close(); a closed PubSub refuses work and never rebinds. */
    private isClosed : boolean = false;
    /** Whether THIS instance ever bound its ipc endpoint — close() must only unlink a file it created. */
    private everBoundIpc : boolean = false;
    

    private readonly batchedPubMessages : Array<Array<any>> = [];

    readonly onlyLogIfIsServer?: string;
    readonly onlyLogRemoteServer?: string;

    constructor(c: BaseServerConfig<any>, onSubMessage: PubSubMessageHandler) {
        if(!isCurveKey(c.publicKey) || !isCurveKey(c.secretKey)) {
            throw new Error(`PubSub for server '${String(c?.id)}' needs a CurveZMQ identity (publicKey + secretKey); BaseServer supplies one.`);
        }
        this.config = c;
        this.id = this.config.id;
        this.onSubMessage = onSubMessage;
        this.zapDomain = ZapAuthority.nextDomain(String(c.id));
        this.publisher = new zmq.Publisher({
            linger: 0,
            curveServer: true,
            curveSecretKey: c.secretKey,
            zapDomain: this.zapDomain,
            // Fail CLOSED: with no ZAP handler answering, refuse the handshake.
            zapEnforceDomain: true,
        });
    }

    private startSubscriber(peerId: string, subscriber: zmq.Subscriber) {
        const myTopic = `${this.config.id}`;
        (async () => {
            try {
                for await (const frames of subscriber) {
                    if (frames[0]?.toString() !== myTopic) continue; // prefix match, not addressed to us
                    let decoded: ServerProtocolArray;
                    try {
                        decoded = PubSub.decodeSubscriptionFrames(frames);
                    } catch (err) {
                        console.error(`[PubSub ${this.config.id}] dropped an undecodable message from peer '${peerId}': ${err instanceof Error ? err.message : String(err)}`);
                        continue;
                    }
                    this.onSubMessage(decoded, peerId);
                }
            } catch {
                // Socket closed under the loop (removeConnection / close).
            }
        })();
    }

    /**
     * Reconstruct the decoded envelope array from raw subscription frames.
     *  - 2 frames `[topic, json]` (publish / publishNoEncode) → `JSON.parse(json)`.
     *  - 3 frames `[topic, json, payload]` (publishRelay) → `JSON.parse(json)` with
     *    the raw payload string appended as the last element.
     * The 3-frame result is BYTE-IDENTICAL to the legacy
     * `JSON.parse(JSON.stringify([...prefix, payload]))` shape — the payload just
     * never got JSON-escaped/unescaped on the way through.
     */
    static decodeSubscriptionFrames(frames: Array<Buffer>): ServerProtocolArray {
        const arr: ServerProtocolArray = JSON.parse(frames[1].toString());
        if (frames.length > 2) arr.push(frames[2].toString());
        return arr;
    }

    private makeTcpAddress(host: string | { host: string, port: string | number }, port?: string | number) {
        ({ host, port } = typeof host === "string" ? {host, port} : host);
        return `tcp://${host}:${port}`;
    }

    /**
     * ipc endpoints live in the cluster's private 0700 directory under the OS
     * temp dir (see `ipcSocketDir`), never the cwd. Both ends derive the path
     * from the advertised host/port and the SHARED cluster id (BaseServer
     * refuses a peer from another cluster), so they agree without negotiation.
     */
    private makeIpcAddress(host: string | { host: string, port:  number }, port?:  number) {
        ({ host, port } = typeof host === "string" ? {host, port} : host);
        return `ipc://${ipcSocketPath(this.config.clusterId, host, port)}`;
    }
    
    /**
     * Where THIS server's publisher binds over tcp: `bindHost` when set, else
     * `host` (the historical bind). Peers never read this — `addSubscriber`
     * connects to a peer's ADVERTISED `host` — which is what lets a server in a
     * container bind `0.0.0.0` while peers reach it by a name. The ipc address
     * below stays on `host`: it is a local file name both ends derive from the
     * advertised config, not an interface.
     */
    get publisherTcpAddress() {
        return this.makeTcpAddress(this.config.bindHost ?? this.config.host, this.config.port);
    }

    get publisherIpcAddress() {
        return this.makeIpcAddress(this.config);
    }

    private async initTcpSocket() {
        if(this.boundTcpSocket) throw new Error(`tcp socket was already bound.`)
        this.isBindingTcpSocket = true;
        this.rebindPublishMethods();
       // console.log('binding tcp socket to', this.publisherTcpAddress, 'on id', this.config.id)
        await this.publisher.bind(this.publisherTcpAddress);
        this.isBindingTcpSocket = false;
        this.boundTcpSocket = true;
        this.rebindPublishMethods();
    }

    private async denitTcpSocket() {
        if(!this.boundTcpSocket) throw new Error(`No tcp socket was bound.`)
        this.isBindingTcpSocket = true;
        this.rebindPublishMethods();
        await this.publisher.unbind(this.publisherTcpAddress);
        this.isBindingTcpSocket = false;
        this.boundTcpSocket = false;
        this.rebindPublishMethods();
    }

    private async initIpcSocket() {
        if(this.boundIpcSocket) throw new Error(`ipc socket was already bound.`)
        this.boundIpcSocket = true;
        this.everBoundIpc = true;
        this.isBindingIpcSocket = true;
        this.rebindPublishMethods();
        await this.publisher.bind(this.publisherIpcAddress);
        this.isBindingIpcSocket = false;
        this.rebindPublishMethods();
    }

    private async denitIpcSocket() {
        if(!this.boundIpcSocket) throw new Error(`No ipc socket was bound.`)
        this.boundIpcSocket = false;
        this.isBindingIpcSocket = true;
        this.rebindPublishMethods();
        await this.publisher.unbind(this.publisherIpcAddress);
        this.isBindingIpcSocket = false;
        this.rebindPublishMethods();
    }


    // publisher.publish will throw an error if the socket is in the middle of binding apparently... so remap the methods to batch the messages
    private rebindPublishMethods() {
        if(this.isBindingIpcSocket || this.isBindingTcpSocket) {
            if(!this.wasUsingBindingPub) {
                const onPub = (serverId: string, data: any) => {
                    this.batchedPubMessages.push([serverId, data]);
                }
                const onRelayPub = (serverId: string | number, prefix: Array<any>, payload: string) => {
                    this.batchedPubMessages.push([serverId, prefix, payload]);
                }
                this.publish = onPub.bind(this);
                this.publishNoEncode = onPub.bind(this);
                this.publishRelay = onRelayPub.bind(this);
                this.wasUsingBindingPub = true;
            }
        } else {
            if(this.wasUsingBindingPub) {
                this.publish = PubSub.prototype.publish.bind(this);
                this.publishNoEncode = PubSub.prototype.publishNoEncode.bind(this);
                this.publishRelay = PubSub.prototype.publishRelay.bind(this);
                this.batchedPubMessages.forEach((entry) => {
                    if(entry.length === 3) {
                        // buffered relay: [serverId, prefix, payload]
                        this.publishRelay(entry[0], entry[1], entry[2]);
                    } else if(typeof entry[1] === "string") {
                        this.publishNoEncode(entry[0], entry[1]);
                    } else {
                        this.publish(entry[0], entry[1]);
                    }
                });
                if (this.batchedPubMessages.length) this.trace(`flushed ${this.batchedPubMessages.length} publish(es) batched during the socket op`);
                this.batchedPubMessages.length = 0;
                this.wasUsingBindingPub = false;
            }
        }
    }

    public async removeConnection(d: BaseServerConfig<any>) {
        const f = this.connected.findIndex(i => i.id === d.id);
        const peer = f > -1 ? this.connected[f] : undefined;
        this.removeSubscriber(d);
        if(f > -1) {
            this.connected.splice(f, 1);
            // The peer leaves this publisher's peer set: its key no longer
            // completes a handshake here (an already-open session ends when
            // the peer's own side closes it).
            if(peer?.publicKey && !this.connected.some(c => c.publicKey === peer.publicKey)) {
                ZapAuthority.shared.revoke(this.zapDomain, peer.publicKey);
            }
            await this.toggleSocketsIfNeeded();
        }
    }

    private addSubscriber(d: BaseServerConfig<any>) {
        const peerId = `${d.id}`;
        // A reconnect to the same id replaces its socket (and therefore its pin).
        this.removeSubscriber(d);
        const address = d.host === this.config.host && this.canUseIpc && !d.forceTcp ? this.makeIpcAddress(d) : this.makeTcpAddress(d);
        const subscriber = new zmq.Subscriber({
            linger: 0,
            curveServerKey: d.publicKey,
            curvePublicKey: this.config.publicKey,
            curveSecretKey: this.config.secretKey,
        });
        subscriber.connect(address);
        subscriber.subscribe(`${this.config.id}`);
        this.subscribers[peerId] = subscriber;
        this.startSubscriber(peerId, subscriber);
        this.redialOnHandshakeFailure(peerId, subscriber, address);
    }

    /** Pending re-dial per peer subscriber (cleared when the peer goes). */
    private readonly redialTimers : {[peerId: string]: ReturnType<typeof setTimeout>} = {};

    /**
     * libzmq NEVER reconnects a socket whose CURVE handshake was refused (ZAP
     * 400, measured): the subscriber just sits there, connected to nothing. A
     * refusal is routinely TRANSIENT here — this peer's publisher was already
     * bound for someone else and has not added our key to its peer set yet
     * (it does so when IT connects to us) — so the subscriber re-dials, with
     * backoff, for as long as the peer is in our connection set. A refusal that
     * persists is a misconfiguration and is reported loudly, once.
     */
    private redialOnHandshakeFailure(peerId: string, subscriber: zmq.Subscriber, address: string) {
        let failures = 0;
        (async () => {
            try {
                for await (const event of subscriber.events) {
                    if (event.type === 'handshake') { failures = 0; continue; }
                    if (!event.type.startsWith('handshake:error')) continue;
                    failures++;
                    if (failures === 20) {
                        console.error(`[PubSub ${this.config.id}] peer '${peerId}' at ${address} has refused our CurveZMQ handshake ${failures} times (${event.type}) — is this server's key in that peer's peer set, and is the peer's key the one we pinned?`);
                    }
                    // First re-dial almost at once (the usual cause — our key
                    // arriving in the peer's set a moment late — is already
                    // fixed), then back off.
                    const delay = failures === 1 ? 10 : Math.min(50 * 2 ** Math.min(failures, 6), 2000);
                    clearTimeout(this.redialTimers[peerId]);
                    const timer = setTimeout(() => {
                        delete this.redialTimers[peerId];
                        if (this.subscribers[peerId] !== subscriber || subscriber.closed) return;
                        this.trace(`re-dial ${peerId} after ${event.type} #${failures}`);
                        try { subscriber.disconnect(address); } catch { /* already detached */ }
                        try { subscriber.connect(address); } catch { /* closed meanwhile */ }
                    }, delay);
                    (timer as any).unref?.();
                    this.redialTimers[peerId] = timer;
                }
            } catch {
                // Observer ends with the socket.
            }
        })();
    }

    /**
     * Admit `publicKeys` to this publisher's peer set AHEAD of connecting to
     * them — for keys an authenticated party (the master) just told us we are
     * about to be linked with. Without it, a peer that dials our already-bound
     * publisher before we connect back is refused once and must re-dial.
     */
    public async admitPeers(publicKeys: string[]): Promise<void> {
        if(this.isClosed) return;
        if(!this.zapOpen) {
            await ZapAuthority.shared.open(this.zapDomain);
            this.zapOpen = true;
        }
        publicKeys.filter(isCurveKey).forEach(k => ZapAuthority.shared.allow(this.zapDomain, k));
    }

    private removeSubscriber(d: BaseServerConfig<any>) {
        const peerId = `${d?.id}`;
        const subscriber = this.subscribers[peerId];
        if(!subscriber) return;
        delete this.subscribers[peerId];
        clearTimeout(this.redialTimers[peerId]);
        delete this.redialTimers[peerId];
        if(!subscriber.closed) subscriber.close();
    }

    public async connect(d: BaseServerConfig<any>, isPreparer=false) : Promise<boolean> {
        if(this.isClosed) {
            throw new Error(`PubSub ${this.config.id} is closed — cannot connect to '${String(d?.id)}'.`);
        }
        // A config with no host/port can never be a reachable peer — most
        // commonly a dynamic-area TEMPLATE that leaked into a peer set. Refuse
        // it loudly here instead of letting zmq throw EINVAL on the garbage
        // address `tcp://undefined:undefined` three frames later.
        if(d?.host == null || d?.port == null) {
            throw new Error(`Cannot connect to server '${String(d?.id)}': config has no host/port (dynamic template?).`);
        }
        // The peer's public key is the pin for BOTH directions: our subscriber
        // only completes a handshake with the publisher holding it, and our
        // publisher only admits a subscriber holding it. No key, no connection —
        // never a plaintext fallback.
        if(!isCurveKey(d.publicKey)) {
            throw new Error(`Cannot connect to server '${String(d.id)}': no public key for it — cluster trust requires every peer's CurveZMQ key (servers.trust.keysFile, or a key the master distributed).`);
        }
        if(!this.zapOpen) {
            await ZapAuthority.shared.open(this.zapDomain);
            this.zapOpen = true;
        }
        if(this.isClosed) {
            throw new Error(`PubSub ${this.config.id} is closed — cannot connect to '${String(d?.id)}'.`);
        }
        ZapAuthority.shared.allow(this.zapDomain, d.publicKey);
        this.connected.push(d);
        await this.toggleSocketsIfNeeded();
       // console.log(`connecting ${this.config.id} to host: ${d.id} by address: ${address}`)
        
            // console.log('REINIT SUBSCRIBER FOR', this.config.id, 'TO', d.id);
        this.addSubscriber(d);
       
        // not 100% why but something with zmq socket is wonky if this executes too quickly in the event loop
        return new Promise(async (resolve, reject) => {
            setTimeout(() => {
                return resolve(true);
            }, 100);
        });
    }


    get canUseIpc() : boolean {
        return process.platform !== "win32" && !this.config.forceTcp
    }

    get needsIpcSocket() : boolean {
        // console.log('connected in', this.config.id, 'is', this.connected.map(c => `${c.host}:${c.id}:${c.forceTcp}`))
        return this.canUseIpc && this.connected.some(c => c.host === this.config.host && !c.forceTcp);
    }

    get needsTcpSocket() : boolean {
        return this.connected.some(c => c.host !== this.config.host || c.forceTcp) || !this.canUseIpc;
    }

    /**
     * Optional lifecycle trace (GOTTI_ZMQ_TRACE=1). This is what caught the
     * skipped-bind interleave in the first place — keep it cheap and available.
     */
    private trace(msg: string) {
        if (!process.env['GOTTI_ZMQ_TRACE']) return;
        // eslint-disable-next-line no-console
        console.error(`[zmqtrace pid=${process.pid} id=${this.config.id} t=${Date.now()}] ${msg}`);
    }

    /**
     * Enqueue a convergence pass on the serialized chain and return when the
     * pass has run. Never lets a rejected op poison the chain — the NEXT pass
     * must still run, or a transient bind error would freeze the lifecycle in
     * exactly the way this serialization exists to prevent.
     */
    private toggleSocketsIfNeeded(): Promise<void> {
        const run = this.socketOpChain.then(() => this.convergeSockets());
        this.socketOpChain = run.catch((err) => {
            this.trace(`converge pass failed: ${err instanceof Error ? err.message : String(err)}`);
        });
        return run;
    }

    /**
     * Drive the socket state to match the CURRENT needs, re-reading them after
     * every awaited op — `connected` is mutated by other call chains while a
     * bind/unbind is in flight, and acting on a stale read is the old skip bug
     * in a different costume. Terminates: each iteration performs one real
     * transition toward the current target, and a closed PubSub's target is
     * "everything unbound".
     */
    /**
     * Run one lifecycle op with a completion trace and a HUNG-OP WATCHDOG.
     *
     * The watchdog is not paranoia: a live capture (final-red1, 2026-08-07)
     * shows a boot where master1's ipc bind START was traced and no completion
     * ever followed — publishes batched forever (rebindPublishMethods holds
     * them while an op is "in flight"), the peer's registration died at its
     * full retry budget, and NOTHING anywhere logged a word. A zmq op that
     * does not settle is a real fault; it must be loud. console.error is
     * deliberate (not trace-gated) — this should shout in ANY run.
     */
    private async runSocketOp(label: string, op: () => Promise<void>): Promise<void> {
        this.trace(label);
        const t0 = Date.now();
        const watchdog = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.error(`[PubSub ${this.config.id}] socket op HUNG >5s: ${label} (started ${new Date(t0).toISOString()}); publishes are batching while this is pending`);
        }, 5000);
        (watchdog as any).unref?.();
        try {
            await op();
            this.trace(`${label} done in ${Date.now() - t0}ms`);
        } finally {
            clearTimeout(watchdog);
        }
    }

    private async convergeSockets(): Promise<void> {
        for (;;) {
            if (this.isClosed) {
                if (this.boundIpcSocket) { await this.runSocketOp(`ipc UNBIND (closing) ${this.publisherIpcAddress}`, () => this.denitIpcSocket()); continue; }
                if (this.boundTcpSocket) { await this.runSocketOp(`tcp UNBIND (closing) ${this.publisherTcpAddress}`, () => this.denitTcpSocket()); continue; }
                return;
            }
            if (this.needsIpcSocket && !this.boundIpcSocket) { await this.runSocketOp(`ipc BIND ${this.publisherIpcAddress}`, () => this.initIpcSocket()); continue; }
            if (!this.needsIpcSocket && this.boundIpcSocket) { await this.runSocketOp(`ipc UNBIND ${this.publisherIpcAddress}`, () => this.denitIpcSocket()); continue; }
            if (this.needsTcpSocket && !this.boundTcpSocket) { await this.runSocketOp(`tcp BIND ${this.publisherTcpAddress}`, () => this.initTcpSocket()); continue; }
            if (!this.needsTcpSocket && this.boundTcpSocket) { await this.runSocketOp(`tcp UNBIND ${this.publisherTcpAddress}`, () => this.denitTcpSocket()); continue; }
            return;
        }
    }

    // Every publish* guards on isClosed: `Publisher.send` returns a promise
    // nobody awaits (deliberate fire-and-forget), and after close() those
    // rejections surfaced as "UNHANDLED REJECTION: Socket is closed" noise in
    // every teardown-heavy run. A send after close is a dropped message either
    // way — drop it silently and traceably instead of as a process-level event.
    publish(serverId: string | number, data: Array<any>): void {
        if (this.isClosed) { this.trace(`publish after close dropped (to ${serverId})`); return; }
        this.publisher.send([`${serverId}`, JSON.stringify(data)]);
    }
    publishNoEncode(serverId: string | number, data: string): void {
        if (this.isClosed) { this.trace(`publish after close dropped (to ${serverId})`); return; }
        this.publisher.send([`${serverId}`, data]);
    }
    /**
     * Relay-envelope send: the `payload` is an ALREADY-serialized client-facing
     * string. It travels as its own ZeroMQ frame instead of being embedded in
     * (and re-escaped by) the outer envelope JSON. Only `prefix`
     * (`[opcode, senderId, ...structuredArgs]`) is stringified. The subscriber
     * reconstructs `[...prefix, payload]` — identical to the legacy framing,
     * minus the double encode/parse of the payload.
     */
    publishRelay(serverId: string | number, prefix: Array<any>, payload: string): void {
        if (this.isClosed) { this.trace(`publish after close dropped (to ${serverId})`); return; }
        this.publisher.send([`${serverId}`, JSON.stringify(prefix), payload]);
    }


    /**
     * Deterministic full quiesce. Order matters and each step exists for a
     * measured reason:
     *
     *  1. `isClosed` first — from here on `connect()` throws and the converge
     *     target becomes "everything unbound", so nothing can rebind after the
     *     cleanup below (the hazard that made earlier unlink attempts unsafe).
     *  2. Remove every connection (detaches subscriber endpoints).
     *  3. Drain the serialized chain — no bind/unbind is in flight when the
     *     sockets close, so close can never race an op (probe-measured: a bind
     *     racing socket teardown throws "No such endpoint").
     *  4. Close the publisher and every per-peer subscriber, and release the
     *     ZAP domain (the shared handler closes with the last one — an open
     *     zmq receive keeps the process alive).
     *  5. Unlink this instance's ipc socket FILE, only if THIS instance ever
     *     bound it. Neither zmq unbind nor close removes the file (measured),
     *     and the integration harness's `getPort` treats the file as "a
     *     publisher owns this path" — leftovers permanently retire ports.
     *
     * Idempotent: a second close() is a no-op.
     */
    async close(): Promise<void> {
        if (this.isClosed) return;
        this.isClosed = true;
        this.trace('CLOSE');
        await Promise.all([...this.connected].map(c => {
            return this.removeConnection(c);
        }));
        // Converge under isClosed ⇒ unbind everything, serialized after any op
        // already in flight.
        await this.toggleSocketsIfNeeded();
        this.publisher.close();
        Object.keys(this.subscribers).forEach(id => this.removeSubscriber({ id } as BaseServerConfig<any>));
        if(this.zapOpen) {
            this.zapOpen = false;
            await ZapAuthority.shared.close(this.zapDomain);
        }
        this.removeIpcSocketFile();
    }

    /**
     * Best-effort removal of the ipc socket file this instance created. Runs
     * ONLY from close(), strictly after everything is unbound and the sockets
     * are closed — never from an unbind (an unbound-then-rebound endpoint must
     * keep its path available to reconnecting peers; deleting it there was
     * measured to break live registrations).
     */
    private removeIpcSocketFile() {
        if (!this.everBoundIpc) return;
        const file = this.publisherIpcAddress.replace(/^ipc:\/\//, '');
        try {
            if (existsSync(file)) unlinkSync(file);
        } catch {
            // A leftover file is a nuisance, not a failure — never throw from teardown.
        }
    }
}